/**
 * Socket event names and shared world constants.
 *
 * ⚠️  MIRRORED FROM `server/game/events.js` — change both together.
 *
 * Create React App cannot import modules from outside `client/src/`, so this
 * file is deliberately duplicated rather than shared. A mismatch fails
 * silently: the client emits an event name nobody listens for, and nothing
 * moves. If movement stops working for no visible reason, diff these two files
 * first.
 */

/**
 * Client → server. "I am mounted and listening, put me in the world."
 *
 * Must be emitted after subscribing, not on connect. The server replies with
 * WELCOME; emitting on connect would race the listener attach.
 */
export const JOIN = 'join';

/** Server → client. Reply to JOIN, sent before any snapshots. */
export const WELCOME = 'welcome';

/** Server → client. Map list with live player counts, for the picker. */
export const ROOMS = 'rooms';

/**
 * Server → client. The players near you, sent every tick.
 *
 * NOT the whole world — the server sends each client only what is close enough
 * to matter, so a snapshot's contents differ per player and change as people
 * move. Entries are `{ i, x, y, f, m }`: short id, position, FACINGS index, and
 * moving as 1/0. Names and colours arrive separately, via ROSTER.
 */
export const SNAPSHOT = 'snapshot';

/**
 * Server → client. Who is in the room, keyed by the short id in snapshots.
 *
 * Sent on join and leave only. `{ players: [{ i, n, c }] }` — short id, display
 * name, colour. These never change, so re-sending them 20 times a second is
 * waste; this is what lets a snapshot entry be five numbers.
 */
export const ROSTER = 'roster';

/**
 * Facing directions, indexed by the `f` field of a snapshot entry.
 *
 * ⚠️  APPEND ONLY. Reordering silently makes every client draw the wrong
 * direction, with no error anywhere.
 */
export const FACINGS = ['down', 'up', 'left', 'right'];

/**
 * Both directions. A player speaking, and the room hearing it.
 *
 * PROXIMITY-SCOPED by the server: you receive chat only from players close
 * enough to be in your snapshot. Chat range and render range are the same
 * number, so every message you hear has a visible head to sit above.
 *
 * Send `{ text }`. Receive `{ i, t }` — short id and text — or `{ sys: 1, t }`
 * for a system reply meant only for you.
 */
export const CHAT = 'chat';

/**
 * Both directions. A canned gesture, scoped exactly like CHAT.
 * Send `{ name }`, receive `{ i, e }` where `e` indexes EMOTES.
 */
export const EMOTE = 'emote';
/**
 * Server -> client. An NPC saying something as it walks past.
 *
 * SEPARATE FROM `CHAT` ON PURPOSE, and the reason is on the client. `Play.js`
 * routes every CHAT payload to TWO destinations — a bubble above the speaker
 * and the scrollback panel — so reusing it would put ambient NPC chatter in the
 * chat log by construction. With a few NPCs pacing about, that log becomes
 * mostly them, burying the messages players actually sent to each other.
 *
 * A player's message is worth keeping because it was said to somebody. Ambient
 * chatter is scenery: it belongs above their head for a few seconds and nowhere
 * else.
 *
 * It also keeps NPC speech out of machinery built for people — mute state, rate
 * limiting and slash commands all hang off the CHAT handler and none of them
 * mean anything for a character the server is driving.
 *
 * Same payload shape as CHAT (`{ i, t }`) so the bubble layer needs no new
 * case: only the routing differs.
 */
export const NPC_SAY = 'npcSay';

/** ⚠️  APPEND ONLY — mirrored from the server, indexed by the `e` field. */
export const EMOTES = ['wave', 'laugh', 'dance', 'sit', 'yes', 'no'];

/** Symbols drawn above the head, parallel to EMOTES. */
export const EMOTE_SYMBOLS = ['♛', '!', '♪', '_', '✓', '✗'];

/** Longest message the server accepts. Rejected beyond this, not truncated. */
export const CHAT_MAX_LENGTH = 100;

/** How long a speech bubble stays on screen, and an emote symbol. */
export const CHAT_BUBBLE_MS = 5000;
export const EMOTE_BUBBLE_MS = 2500;

/**
 * Client -> server. "Talk to whoever I am standing next to."
 *
 * Carries at most a node id, never a target: the server decides WHO is in
 * range, the same way it decides everything else about position. A client that
 * named its own target could talk to an NPC across the map.
 */
export const INTERACT = 'interact';

/** Server -> client. One node of a conversation. */
export const DIALOGUE = 'dialogue';

/**
 * Server -> everyone in the room. Somebody's name is now on a building.
 *
 * `{ zoneId, owner }`. The client already holds the zone list from `WELCOME`,
 * so this is the smallest thing that keeps it true — re-welcoming would rebuild
 * the map, roster and camera to change one label, and the world would visibly
 * blink because a stranger claimed a shop.
 */
export const CLAIMED = 'claimed';

/**
 * Server -> everyone in the room. Which places are shut, and why.
 *
 * `{ closed: { [zoneId]: reason } }`, and ONLY THE SHUT ONES — open is the
 * default, so saying so would be bytes spent on nothing.
 *
 * A COMPLETE SET, NOT A DELTA, unlike `CLAIMED` above. Claiming happens to one
 * building at a time; nine o'clock happens to every shop on the map at once. The
 * engine replaces its map wholesale on receipt, which also means a client that
 * missed one heals on the next rather than keeping a shop shut all evening.
 */
export const CLOSURES = 'closures';

/**
 * Server -> you alone, once per connection. What changed while you were gone.
 *
 * `{ awayFor, neighbours: {shown, more}, places: {shown, more} }`.
 *
 * ARRIVES AT MOST ONCE, and often not at all — there is nothing to say on a
 * first visit, after a refresh, or when the world was quiet while you were out.
 * A client that never receives this is in the normal case, not a broken one.
 */
export const WELCOME_BACK = 'welcomeBack';

/**
 * Why a place is shut.
 *
 * MIRRORED from the server, which decides which one applies — see
 * server/game/businessHours.js. `closedLabel.js` turns these into the words
 * drawn over a building, so a rename here is a two-file change.
 */
export const CLOSED_REASONS = ['hours', 'temporary', 'permanent', 'soon'];

/**
 * How close you must stand to talk, in world units.
 *
 * MIRRORED, because the client draws the prompt and the server enforces the
 * rule — if they disagree you get a prompt that does nothing when pressed.
 *
 * Matches SIGN_READ_RANGE, which is the established answer to "close enough to
 * read something in this world". Deliberately NOT the interest box: that is
 * 384x244, most of a screen, and shouting across a courtyard is not a
 * conversation.
 */
export const TALK_RANGE = 56;

/**
 * Which kinds of building a player may make their own.
 *
 * MIRRORED, exactly as `TALK_RANGE` is and for the same reason: this decides
 * whether the prompt is offered, and the server decides whether the claim is
 * allowed. If they disagree you get an instruction that does nothing.
 *
 * A shop has somebody behind the counter; a landmark or an area is a monument
 * or a park. Neither is somewhere to live.
 */
export const CLAIMABLE_KINDS = ['house', 'building'];

/**
 * Server -> everyone in the room. A coin was picked up.
 *
 * `{ c: coinId, i: pickerShortId, n: amount }`.
 *
 * ROOM-WIDE, not scoped to who can see it, because every client holds the whole
 * coin list from `WELCOME` and so every client has to hear that one is gone.
 *
 * THE PICKER'S SHORT ID IS WHY THERE IS NO SECOND EVENT for the "+1": float it
 * when that id is on screen, ignore it when it is not. A "+1" over somebody you
 * cannot see has nowhere to go.
 */
export const COIN_TAKEN = 'coinTaken';

/**
 * Server -> everyone in the room. A coin appeared.
 *
 * `{ c: coinId, x, y }` in FOOT SPACE — where it sits on the ground.
 *
 * NOT AN ENTITY CENTRE. A player's snapshot `y` is their centre, with their
 * feet `FOOT_OFFSET_Y` below it. Drawing a coin as if it were a player puts
 * every one of them seven pixels north of where it can actually be picked up.
 */
export const COIN_SPAWNED = 'coinSpawned';

/**
 * Server -> everyone in the room. Every coin on the floor, replaced wholesale.
 *
 * `{ coins: [{ c, x, y }, ...] }` — the same entries `WELCOME` carries, so a
 * join and a toggle go through one code path (`engine.setCoins`).
 *
 * A COMPLETE SET, NOT A DELTA, unlike the two events above — the same
 * distinction `CLOSURES` draws against `CLAIMED`. Sent when an admin turns
 * money on or off, which changes every coin at once. **An empty array is a real
 * payload**: it is what "off" looks like, and treating it as "no news" would
 * leave uncollectable coins on screen forever.
 */
export const COINS = 'coins';

/**
 * Server -> you alone. What you have.
 *
 * `{ coins: 243 }` — whole coins, no cents, no formatting.
 *
 * NEVER ROOM-WIDE. Nobody else's balance is ever on the wire, which is how
 * "is money visible to others?" stays an open question rather than an accident.
 *
 * Arrives after `WELCOME` on join and after every pickup. Survives a map
 * change — do not blank it on `WELCOME`, or it flickers on every portal.
 */
export const BALANCE = 'balance';

/**
 * What one coin is worth.
 *
 * MIRRORED for rendering, though `COIN_TAKEN` carries `n` on the wire anyway —
 * so the server can vary it later without a client change.
 */
export const COIN_VALUE = 1;

/**
 * Client -> server. "I have the map open" / "I closed it".
 *
 * The overview below is the one payload in this protocol that ignores interest
 * management, so it is the one that has to be asked for. A client with the map
 * closed receives nothing at all.
 */
export const OVERVIEW_SUB = 'overviewSub';

/**
 * Server -> client. Everybody in the room, for the minimap.
 *
 * DELIBERATELY IGNORES INTEREST. Every other payload sends you only what is
 * near you — that is what makes 100 players affordable. A map showing only your
 * own corner would be a radar, not a map, so this is the exception, and it is
 * opt-in precisely because it is one.
 *
 * ARRAY-ENCODED, IN TILE COORDINATES, and both parts are load-bearing.
 * `docs/interest-management.md` parks array encoding with "revisit if a map
 * ever holds enough players to need it" — this is that moment. Measured against
 * the 6.0 KB/s per-client baseline, objects would add 73% for a client with the
 * map open; arrays add 24%. Production is egress-bound, so that difference is
 * the feature being affordable or not.
 *
 *   { p: [[shortId, tileX, tileY], ...],   // people
 *     n: [[shortId, tileX, tileY], ...] }  // NPCs
 *
 * Split into two arrays rather than tagging each entry, so "which kind" costs
 * nothing per entity. Tile coordinates because a minimap draws in tiles and
 * sub-tile precision is invisible at dot size.
 */
export const OVERVIEW = 'overview';

/**
 * How often the overview goes out, in Hz.
 *
 * Deliberately far below the 20Hz tick: a dot crossing a minimap does not need
 * to be smooth, and this is the payload that costs the scarce resource.
 */
export const OVERVIEW_HZ = 2;

/** Client → server. The player's currently held input vector. */
export const INPUT = 'input';

/**
 * The viewport, in world units. The canvas is exactly this size.
 *
 * One world unit == one logical pixel. The canvas element is 320x180 and is
 * CSS-scaled 3x to 960x540 on screen — that scaling, plus
 * `image-rendering: pixelated`, is what keeps pixel art crisp. Tiles land at
 * 48px and character cells at 96px, matching Pizza Legends.
 *
 * This is the VIEW, not the world. The world is as large as the loaded map;
 * the camera keeps the local player centred and clamps at the map's edges.
 */
export const VIEW_WIDTH = 320;
export const VIEW_HEIGHT = 180;

/**
 * How many CSS pixels one world unit occupies on screen.
 *
 * CLIENT-ONLY — the server has no opinion about presentation, so this is not
 * mirrored (same as INTERPOLATION_DELAY_MS below).
 *
 * The art canvas stays 320x180 and is stretched by this factor with
 * `image-rendering: pixelated`, which is what keeps sprite pixels hard-edged.
 * Text is drawn on a separate full-resolution overlay and multiplies its
 * coordinates and font sizes by this, so a 6-unit label renders as an 18px
 * glyph: the same apparent size, but actually sharp rather than a magnified
 * antialiased smudge.
 */
export const VIEW_SCALE = 3;

/** One map tile, in world units. Two tiles per character width. */
export const TILE_SIZE = 16;

/**
 * How far in the past remote players are rendered, in milliseconds.
 *
 * Snapshots arrive at 20Hz (every 50ms) but the canvas redraws at ~60fps, so
 * without this each position holds for three frames and everything steps.
 * Rendering slightly behind means there is always a *pair* of snapshots
 * bracketing the current render time to interpolate between.
 *
 * 100ms is two tick intervals — enough cushion that a late packet still
 * arrives before it is needed. Lower and jitter starts causing stutters;
 * higher and remote players lag noticeably behind where they really are.
 *
 * The LOCAL player is deliberately exempt. See SnapshotBuffer.
 */
export const INTERPOLATION_DELAY_MS = 100;

/**
 * The character's nominal body size, in world units.
 *
 * NOT the collision box — the server collides on a small footprint at the feet
 * (FOOT_* below). This is a visual measure: nameplate and bubble offsets, and
 * the fallback square drawn when a sheet fails to load.
 */
export const PLAYER_SIZE = 24;

/** One sprite frame, in world units. Also the sheet's cell size. */
export const SPRITE_SIZE = 32;

/**
 * THE COLLISION BOX the server enforces: a small footprint at the feet, offset
 * below the entity centre, spanning y+2 to y+12.
 *
 * The head and torso sit outside it deliberately and may overlap wall tiles
 * above — that is what lets you walk up into a doorway instead of catching on
 * the lintel a tile early. Mirrored here so the shadow ellipse under the feet
 * stays the same width as the thing that actually collides.
 */
export const FOOT_HALF_W = 9;
export const FOOT_HALF_H = 5;
export const FOOT_OFFSET_Y = 7;
